/**
 * The production list — and, today, the empty state.
 *
 * `GET /v1/productions` returns `[]` on production right now. That is a
 * legitimate product state, not a placeholder waiting to be replaced, so it
 * is built first and built properly: it names the next action and fabricates
 * no statistic to fill the space. A dashboard that invents a metric from no
 * data is the failure this whole system is shaped to avoid.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { canWrite } from '../../api/client';
import { modeller, type ProductionStatus, type ProductionSummary } from '../api';
import { PageHeader } from '../shell/Shell';
import { Empty, ErrorState, Loading } from '../shell/states';
import './productions.css';

/* Keyed on the real enum. `shooting` is the only live state and gets the cyan
   pulse; `confirmed` is committed but not yet running; the rest are quiet.
   Anything unmapped falls through, but nothing should. */
const STATUS_BADGE: Record<ProductionStatus, string> = {
  shooting: 'pem-badge--live pem-badge--pill',
  confirmed: 'pem-badge--info pem-badge--pill',
  quoted: 'pem-badge--outline',
  prospect: 'pem-badge--assumed',
  wrapped: 'pem-badge--quiet',
  cancelled: 'pem-badge--quiet',
  lost: 'pem-badge--quiet',
};

/** Rendered only when the session can actually create one. */
function NewButton() {
  if (!canWrite()) return null;
  return (
    <Link className="pem-btn" to="/modeller/productions/new">
      Start a production
    </Link>
  );
}

type State =
  | { t: 'loading' }
  | { t: 'error'; e: unknown }
  | { t: 'ok'; rows: ProductionSummary[] };

export function Productions() {
  const [state, setState] = useState<State>({ t: 'loading' });

  useEffect(() => {
    let live = true;
    modeller.productions()
      .then((rows) => { if (live) setState({ t: 'ok', rows }); })
      .catch((e) => { if (live) setState({ t: 'error', e }); });
    return () => { live = false; };
  }, []);

  if (state.t === 'loading') {
    return <><PageHeader title="Productions" /><Loading what="productions" /></>;
  }
  if (state.t === 'error') {
    return <><PageHeader title="Productions" /><ErrorState what="productions" error={state.e} /></>;
  }

  const { rows } = state;
  if (!rows.length) {
    return (
      <>
        <PageHeader title="Productions" actions={<NewButton />} />
        <Empty
          title="No productions yet"
          body={
            'A production is the job: it holds the days it shoots, the ' +
            'equipment on them, and the energy estimates that come out. ' +
            'Start one and the rest follows from it.'
          }
          /*
            AN EMPTY STATE MUST END IN SOMETHING THE USER CAN DO.
            When the session can create, this is a working button. When it
            cannot, there is no button — only a sentence saying who to ask.
            A control that exists and refuses makes the product look broken
            rather than unfinished.
          */
          action={
            canWrite()
              ? <Link className="pem-btn" to="/modeller/productions/new">
                  Start a production
                </Link>
              : <p className="pem-state__detail">
                  Your access key opens the modeller for viewing. To start a
                  production you need an editing key — ask Equoia for one, then
                  sign in again and add it.
                </p>
          }
        />
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="Productions"
        count={`${rows.length} ${rows.length === 1 ? 'job' : 'jobs'}`}
        actions={<NewButton />}
      />
      <div className="pem-list">
        {rows.map((p) => (
          <Link key={p.slug} className="pem-prod" to={`/modeller/productions/${p.slug}`}>
            <div className="pem-prod__main">
              <div className="pem-prod__head">
                <h3>{p.name}</h3>
                <span className={`pem-badge ${STATUS_BADGE[p.status] ?? 'pem-badge--quiet'}`}>
                  {p.status}
                </span>
              </div>
              <div className="pem-prod__meta">
                {p.production_company && <span>{p.production_company}</span>}
                <span>{p.timezone}</span>
              </div>
            </div>
            {/* NO DATE RANGE AND NO UNIT-DAY COUNT HERE. The list endpoint does
                not populate shoot_days, so anything derived from it would read
                "No dates yet · 0 unit-days" for a production with four. The
                schedule is on the detail screen, where the data exists. */}
            <div className="pem-prod__side">
              <span className="pem-provenance">registered {p.created_at.slice(0, 10)}</span>
            </div>
          </Link>
        ))}
      </div>
    </>
  );
}
